import { MRT_SortingState } from '@talentmesh/table';
import { useCallback, useMemo, useState } from 'react';
import { ApplicantRecruitmentOverviewResponse } from '@/Models/CandidateOverview';
import ApplicantsOverviewSortByEnum from '../../../Models/ApplicantsOverviewSortByEnum';
import { CandidatesOverviewContextType } from '../../CandidatesOverviewContext/CandidatesOverviewContext';
import { useCandidatesOverviewQuery } from '../../CandidatesOverviewContext/utils/useCandidatesOverviewQueryParams';

const useManualSorting = (sort: CandidatesOverviewContextType['sort']) => {
    const sortByQuery = useCandidatesOverviewQuery('sortBy');
    const initialSortingState = useMemo<
        (MRT_SortingState[0] & { id: keyof ApplicantRecruitmentOverviewResponse })[]
    >(() => {
        switch (+sortByQuery! as ApplicantsOverviewSortByEnum) {
            case ApplicantsOverviewSortByEnum.AppliedAtDesc:
                return [{ id: 'appliedAt', desc: true }];
            case ApplicantsOverviewSortByEnum.AppliedAtAsc:
                return [{ id: 'appliedAt', desc: false }];
            case ApplicantsOverviewSortByEnum.AptitudeScoreDesc:
                return [{ id: 'aptitudePercentileScore', desc: true }];
            case ApplicantsOverviewSortByEnum.AptitudeScoreAsc:
                return [{ id: 'aptitudePercentileScore', desc: false }];
            case ApplicantsOverviewSortByEnum.CompletedAtDesc:
                return [{ id: 'completedAt', desc: true }];
            case ApplicantsOverviewSortByEnum.CompletedAtAsc:
                return [{ id: 'completedAt', desc: false }];
            case ApplicantsOverviewSortByEnum.EmotionalIntelligenceScoreDesc:
                return [{ id: 'emotionalIntelligencePercentileScore', desc: true }];
            case ApplicantsOverviewSortByEnum.EmotionalIntelligenceScoreAsc:
                return [{ id: 'emotionalIntelligencePercentileScore', desc: false }];
            case ApplicantsOverviewSortByEnum.ExperienceCriteriaScoreDesc:
                return [{ id: 'experienceCriteriaScore', desc: true }];
            case ApplicantsOverviewSortByEnum.ExperienceCriteriaScoreAsc:
                return [{ id: 'experienceCriteriaScore', desc: false }];
            case ApplicantsOverviewSortByEnum.InterviewingAtDesc:
                return [{ id: 'interviewingAt', desc: true }];
            case ApplicantsOverviewSortByEnum.InterviewingAtAsc:
                return [{ id: 'interviewingAt', desc: false }];
            case ApplicantsOverviewSortByEnum.TalentScoreDesc:
                return [{ id: 'talentScore', desc: true }];
            case ApplicantsOverviewSortByEnum.TalentScoreAsc:
                return [{ id: 'talentScore', desc: false }];
            case ApplicantsOverviewSortByEnum.SkillsScoreDesc:
                return [{ id: 'skillsPercentileScore', desc: true }];
            case ApplicantsOverviewSortByEnum.SkillsScoreAsc:
                return [{ id: 'skillsPercentileScore', desc: false }];
            case ApplicantsOverviewSortByEnum.PersonalityScoreDesc:
                return [{ id: 'personalityPercentileScore', desc: true }];
            case ApplicantsOverviewSortByEnum.PersonalityScoreAsc:
                return [{ id: 'personalityPercentileScore', desc: false }];
            case ApplicantsOverviewSortByEnum.StartedAtDesc:
                return [{ id: 'startedAt', desc: true }];
            case ApplicantsOverviewSortByEnum.StartedAtAsc:
                return [{ id: 'startedAt', desc: false }];
            case ApplicantsOverviewSortByEnum.InvitedForInterviewAtDesc:
                return [{ id: 'invitedForInterviewAt', desc: true }];
            case ApplicantsOverviewSortByEnum.InvitedForInterviewAtAsc:
                return [{ id: 'invitedForInterviewAt', desc: false }];
            default:
                return [];
        }
    }, []);
    const [sorting, setSortingState] = useState<MRT_SortingState>(initialSortingState);

    const columnSortToEnum = useCallback(({ desc, id }: MRT_SortingState[0]) => {
        const sortBy = ApplicantsOverviewSortByEnum;
        switch (id as keyof ApplicantRecruitmentOverviewResponse) {
            case 'talentScore':
                return desc ? sortBy.TalentScoreDesc : sortBy.TalentScoreAsc;
            case 'experienceCriteriaScore':
                return desc ? sortBy.ExperienceCriteriaScoreDesc : sortBy.ExperienceCriteriaScoreAsc;
            case 'aptitudePercentileScore':
                return desc ? sortBy.AptitudeScoreDesc : sortBy.AptitudeScoreAsc;
            case 'emotionalIntelligencePercentileScore':
                return desc ? sortBy.EmotionalIntelligenceScoreDesc : sortBy.EmotionalIntelligenceScoreAsc;
            case 'personalityPercentileScore':
                return desc ? sortBy.PersonalityScoreDesc : sortBy.PersonalityScoreAsc;
            case 'skillsPercentileScore':
                return desc ? sortBy.SkillsScoreDesc : sortBy.SkillsScoreAsc;
            case 'appliedAt':
                return desc ? sortBy.AppliedAtDesc : sortBy.AppliedAtAsc;
            case 'startedAt':
                return desc ? sortBy.StartedAtDesc : sortBy.StartedAtAsc;
            case 'completedAt':
                return desc ? sortBy.CompletedAtDesc : sortBy.CompletedAtAsc;
            case 'invitedForInterviewAt':
                return desc ? sortBy.InvitedForInterviewAtDesc : sortBy.InvitedForInterviewAtAsc;
            case 'interviewingAt':
                return desc ? sortBy.InterviewingAtDesc : sortBy.InterviewingAtAsc;
            default:
                throw new Error('invalid column');
        }
    }, []);

    const onSortingChange = useCallback<React.Dispatch<React.SetStateAction<MRT_SortingState>>>(
        (dispatcher) => {
            let newSortingState: MRT_SortingState;
            if (typeof dispatcher === 'function') {
                const result = dispatcher(sorting);
                setSortingState(result);
                newSortingState = result;
            } else {
                setSortingState(dispatcher);
                newSortingState = dispatcher;
            }

            sort(newSortingState.length ? columnSortToEnum(newSortingState[0]) : undefined);
        },
        [sorting, setSortingState],
    );

    return { sorting, onSortingChange };
};

export default useManualSorting;
